/**
 * EventFoundry Tracker Script
 * Embedded script that fetches event definitions and tracks user interactions
 */

import { initTracking } from './tracker.js';
import { isInIframe, initEditorMode } from './editor-mode.js';
import { devLog } from './dev-logger.js';
import { getAdapter } from './adapters/registry.js';

// Auto-detect API endpoint based on environment
const isLocal = window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const API_ENDPOINT = isLocal
  ? 'http://localhost/api/tracking/events'
  : 'https://eventfoundry.app/api/tracking/events';

const FRESH_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
const STALE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
let hasLoggedCacheFallbackWarning = false;
let hasLoggedSafeEmptyWarning = false;

/**
 * Generate site-specific cache key for event data
 * @param {string} siteKey - Site key
 * @returns {string} Cache key
 */
function getCacheKey(siteKey) {
  return `eventfoundry_cache_${siteKey}`;
}

/**
 * Log cache fallback warning at most once per page load
 * @param {string} reason - Fallback reason
 * @param {number} cacheAgeHours - Approximate cache age in hours
 */
function warnCacheFallbackOnce(reason, cacheAgeHours) {
  if (hasLoggedCacheFallbackWarning) return;
  console.warn(`EventFoundry: ${reason}, using cached data from ${cacheAgeHours}h ago`);
  hasLoggedCacheFallbackWarning = true;
}

/**
 * Log safe-empty warning at most once per page load
 * @param {string} reason - Fallback reason
 */
function warnSafeEmptyOnce(reason) {
  if (hasLoggedSafeEmptyWarning) return;
  console.warn(`EventFoundry: ${reason}, using safe-empty configuration`);
  hasLoggedSafeEmptyWarning = true;
}

/**
 * Extract configuration from script tag attributes
 * @returns {Object|null} Config object with siteKey and devMode, or null if script tag not found
 */
function getScriptConfig() {
  const currentScript = document.currentScript;
  if (currentScript?.getAttribute('data-site-key')) {
    return buildScriptConfig(currentScript);
  }

  const explicitScript = document.getElementById('eventfoundry-tracker');
  if (explicitScript?.tagName === 'SCRIPT' && explicitScript.getAttribute('data-site-key')) {
    return buildScriptConfig(explicitScript);
  }

  const scriptCandidates = Array.from(document.querySelectorAll('script[data-site-key]'));
  if (scriptCandidates.length === 0) {
    console.error('EventFoundry: Could not find script tag');
    return null;
  }

  if (scriptCandidates.length > 1) {
    console.warn(
      'EventFoundry: Multiple script[data-site-key] tags found; using last tag. ' +
      'Set id="eventfoundry-tracker" for deterministic selection.'
    );
  }

  const selectedScript = scriptCandidates[scriptCandidates.length - 1];
  return buildScriptConfig(selectedScript);
}

function buildScriptConfig(script) {
  const devModeAttr = script?.getAttribute('data-dev-mode');

  // Auto-detect dev mode: localhost OR explicit data-dev-mode="true"
  // Can be overridden with data-dev-mode="false" to force production mode on localhost
  const devMode = devModeAttr === 'false' ? false : (isLocal || devModeAttr === 'true');

  return {
    siteKey: script.getAttribute('data-site-key'),
    devMode
  };
}

/**
 * Validate destination configuration
 * @param {Object} config - Destination config object
 * @returns {boolean} - True if valid
 */
function validateDestinationConfig(config) {
  const { destination, credentials, enabled } = config;

  // Required fields
  if (!destination || typeof destination !== 'string') {
    console.error('EventFoundry: Invalid destination config - missing destination name');
    return false;
  }

  if (typeof enabled !== 'boolean') {
    console.error(`EventFoundry: Invalid ${destination} config - missing enabled field`);
    return false;
  }

  if (!credentials || typeof credentials !== 'object') {
    console.error(`EventFoundry: Invalid ${destination} config - missing credentials`);
    return false;
  }

  // GA4-specific validation
  if (destination === 'GA4') {
    const { measurementId } = credentials;
    if (!measurementId || !measurementId.match(/^G-[A-Z0-9]+$/)) {
      console.error(`EventFoundry: Invalid GA4 measurement ID: ${measurementId}`);
      return false;
    }
  }

  // Meta Pixel-specific validation
  if (destination === 'META_PIXEL') {
    const { pixelId } = credentials;
    if (!pixelId || !pixelId.match(/^\d+$/)) {
      console.error(`EventFoundry: Invalid Meta Pixel ID: ${pixelId}`);
      return false;
    }
  }

  return true;
}

/**
 * Initialize all enabled destinations
 * @param {Array} destinationConfigs - Array of destination configs from API
 * @returns {Promise<Object>} - Object mapping destination names to initialization status
 */
async function initializeDestinations(destinationConfigs) {
  const initResults = {};

  // Filter to enabled and valid destinations only
  const enabledConfigs = destinationConfigs.filter(config => config.enabled);
  const validConfigs = enabledConfigs.filter(config => validateDestinationConfig(config));

  if (enabledConfigs.length === 0) {
    console.warn('EventFoundry: No destinations enabled');
    return initResults;
  }

  if (validConfigs.length === 0) {
    console.error('EventFoundry: No valid destination configurations found');
    return initResults;
  }

  if (validConfigs.length < enabledConfigs.length) {
    console.warn(`EventFoundry: ${enabledConfigs.length - validConfigs.length} destination(s) skipped due to invalid configuration`);
  }

  console.log(`EventFoundry: Initializing ${validConfigs.length} destination(s)`);

  // Initialize each destination
  for (const config of validConfigs) {
    const { destination, credentials } = config;
    const adapter = getAdapter(destination);

    if (!adapter) {
      initResults[destination] = false;
      continue;
    }

    try {
      const success = await adapter.initialize(credentials);
      initResults[destination] = success;

      if (success) {
        console.log(`✓ ${destination} initialized`);
      } else {
        console.error(`✗ ${destination} initialization failed`);
      }
    } catch (error) {
      console.error(`EventFoundry: ${destination} initialization error:`, error);
      initResults[destination] = false;
    }
  }

  return initResults;
}

/**
 * Main initialization function
 * Fetches event definitions and sets up tracking
 */
async function init() {
  // Get script configuration
  const config = getScriptConfig();
  if (!config || !config.siteKey) {
    console.error('EventFoundry: No site key provided');
    return;
  }

  const { siteKey, devMode } = config;

  // Log initialization (1 line)
  console.log(`✓ EventFoundry tracker loaded (v${__VERSION__})`);

  // Check if we're in an iframe (potential editor mode)
  if (isInIframe()) {
    // Initialize editor mode detection, passing reload callback
    initEditorMode(reloadEventDefinitions);
  }

  // Log dev mode status (uses devLog to prevent stripping by build)
  devLog(devMode, '⚠ Dev mode: Events disabled');
  devLog(!devMode, '✓ Production mode: Events enabled');

  try {
    // Fetch event configuration
    const data = await fetchEventDefinitions(siteKey);

    // Initialize all enabled destinations
    const initResults = await initializeDestinations(data.destinationConfigs);

    // Check if at least one destination initialized successfully
    const hasAnyDestination = Object.values(initResults).some(success => success);

    if (!hasAnyDestination && !isInIframe()) {
      console.warn('EventFoundry: No destinations available - tracking disabled');
      return;
    }

    // Extract events array from response
    const events = data.events;

    // Initialize event tracking
    const trackingInitialized = initTracking(events, devMode, initResults);

    // If tracking failed due to invalid selectors and data came from cache,
    // clear cache and try fetching fresh data from API
    if (!trackingInitialized && data._fromCache) {
      console.log('EventFoundry: Tracking failed with cached data, clearing cache and refetching...');
      clearCache(siteKey);

      // Fetch fresh data (bypassing cache)
      const freshData = await fetchEventDefinitions(siteKey);
      const freshInitResults = await initializeDestinations(freshData.destinationConfigs);
      const freshEvents = freshData.events;

      // Try initializing with fresh data
      initTracking(freshEvents, devMode, freshInitResults);
    }
  } catch (error) {
    console.error('EventFoundry: Failed to initialize:', error);
  }
}

/**
 * Fetch event definitions with ETag-based caching
 * @param {string} siteKey - Site key for fetching event definitions
 * @returns {Promise<Object>} Event definitions response object with events, destinationConfigs, and version
 */
async function fetchEventDefinitions(siteKey) {
  // Get cached data and ETag
  const cached = getFromCache(siteKey);
  const cachedETag = getETagFromCache(siteKey);

  // Prepare request headers
  const headers = {};
  if (cachedETag) {
    headers['If-None-Match'] = cachedETag;
  }

  let response;
  try {
    // Fetch from API with conditional request
    response = await fetch(`${API_ENDPOINT}?siteKey=${siteKey}`, {
      headers: headers,
      cache: 'no-store'
    });
  } catch (error) {
    // Handle network errors (DNS failures, timeouts, offline)
    // Try to use cached data as fallback (stale allowed up to STALE_TTL_MS)
    if (isUsableCachedPayload(cached)) {
      const cacheAgeHours = Math.round(getCacheAgeMs(cached) / (1000 * 60 * 60));
      warnCacheFallbackOnce(`Network error (${error.message})`, cacheAgeHours);
      return toConsumableCachedPayload(cached);
    }

    warnSafeEmptyOnce(`Network error (${error.message})`);
    return getSafeEmptyPayload();
  }

  // Handle 304 Not Modified - use cached data
  if (response.status === 304) {
    if (isFreshCachedPayload(cached)) {
      return toConsumableCachedPayload(cached);
    }

    try {
      // Fallback: if cache is missing/stale/unusable, fetch again without ETag
      const fallbackResponse = await fetch(`${API_ENDPOINT}?siteKey=${siteKey}`, {
        cache: 'no-store'
      });

      if (!fallbackResponse.ok) {
        if (isUsableCachedPayload(cached)) {
          const cacheAgeHours = Math.round(getCacheAgeMs(cached) / (1000 * 60 * 60));
          warnCacheFallbackOnce(`API unavailable (${fallbackResponse.status})`, cacheAgeHours);
          return toConsumableCachedPayload(cached);
        }
        warnSafeEmptyOnce(`API unavailable (${fallbackResponse.status})`);
        return getSafeEmptyPayload();
      }

      const data = await fallbackResponse.json();

      if (!isValidApiPayload(data)) {
        if (isUsableCachedPayload(cached)) {
          const cacheAgeHours = Math.round(getCacheAgeMs(cached) / (1000 * 60 * 60));
          warnCacheFallbackOnce('Invalid API response schema', cacheAgeHours);
          return toConsumableCachedPayload(cached);
        }
        warnSafeEmptyOnce('Invalid API response schema');
        return getSafeEmptyPayload();
      }

      // Prevent cache rollback during deploy propagation
      if (isUsableCachedPayload(cached) && data.version < cached.version) {
        return toConsumableCachedPayload(cached);
      }

      const etag = fallbackResponse.headers.get('etag');
      saveToCache(siteKey, data, etag);
      return data;
    } catch (error) {
      if (isUsableCachedPayload(cached)) {
        const cacheAgeHours = Math.round(getCacheAgeMs(cached) / (1000 * 60 * 60));
        warnCacheFallbackOnce('304 fallback fetch failed', cacheAgeHours);
        return toConsumableCachedPayload(cached);
      }
      warnSafeEmptyOnce('304 fallback fetch failed');
      return getSafeEmptyPayload();
    }
  }

  // Handle API errors with stale-cache fallback
  if (!response.ok) {
    // Try to use cached data as fallback (stale allowed up to STALE_TTL_MS)
    if (isUsableCachedPayload(cached)) {
      const cacheAgeHours = Math.round(getCacheAgeMs(cached) / (1000 * 60 * 60));
      warnCacheFallbackOnce(`API unavailable (${response.status})`, cacheAgeHours);
      return toConsumableCachedPayload(cached);
    }

    warnSafeEmptyOnce(`API unavailable (${response.status})`);
    return getSafeEmptyPayload();
  }

  // Handle 200 OK - parse new data and update cache
  let data;
  try {
    data = await response.json();
  } catch (error) {
    warnSafeEmptyOnce('Invalid JSON response');
    return getSafeEmptyPayload();
  }

  // Validate response structure
  if (!isValidApiPayload(data)) {
    warnSafeEmptyOnce('Invalid response schema');
    return getSafeEmptyPayload();
  }

  // Prevent cache rollback during deploy propagation
  if (isUsableCachedPayload(cached) && data.version < cached.version) {
    return toConsumableCachedPayload(cached);
  }

  // Warn if no destinations configured
  if (data.destinationConfigs.length === 0) {
    console.warn('EventFoundry: No destinations configured - events will not be sent');
  }

  const etag = response.headers.get('etag');
  saveToCache(siteKey, data, etag);

  return data;
}

/**
 * Get cached event data from localStorage
 * @param {string} siteKey - Site key
 * @returns {Object|null} Cached definitions or null
 */
function getFromCache(siteKey) {
  try {
    const cacheKey = getCacheKey(siteKey);
    const item = localStorage.getItem(cacheKey);
    if (!item) return null;

    const cacheData = JSON.parse(item);

    // Return cache data with metadata intact (needed for stale-cache fallback)
    // The _etag, _cachedAt fields will be stripped by consumers when needed
    return cacheData;
  } catch (error) {
    console.warn('EventFoundry: Cache read failed:', error);
    return null;
  }
}

/**
 * Build a safe no-op payload to prevent tracker failures
 * @returns {Object} Safe empty payload
 */
function getSafeEmptyPayload() {
  return {
    events: [],
    destinationConfigs: [],
    version: 0,
    _fromSafeEmpty: true
  };
}

/**
 * Validate API/cache payload structure
 * @param {Object} data - Response data
 * @returns {boolean} True if shape is valid
 */
function isValidApiPayload(data) {
  return !!data &&
    Array.isArray(data.events) &&
    Array.isArray(data.destinationConfigs) &&
    typeof data.version === 'number';
}

/**
 * Get cache age in milliseconds
 * @param {Object} cached - Cached payload
 * @returns {number} Cache age in ms
 */
function getCacheAgeMs(cached) {
  if (typeof cached?._cachedAt !== 'number') return Infinity;
  return Date.now() - cached._cachedAt;
}

/**
 * Check if cached payload is usable for stale fallback
 * @param {Object|null} cached - Cached payload
 * @returns {boolean} True if usable and within stale TTL
 */
function isUsableCachedPayload(cached) {
  return isValidApiPayload(cached) &&
    Number.isFinite(cached._cachedAt) &&
    getCacheAgeMs(cached) <= STALE_TTL_MS;
}

/**
 * Check if cached payload is fresh enough for 304 fast-path
 * @param {Object|null} cached - Cached payload
 * @returns {boolean} True if fresh and usable
 */
function isFreshCachedPayload(cached) {
  return isUsableCachedPayload(cached) &&
    getCacheAgeMs(cached) <= FRESH_TTL_MS;
}

/**
 * Remove cache metadata and flag source
 * @param {Object} cached - Cached payload
 * @returns {Object} Consumable payload
 */
function toConsumableCachedPayload(cached) {
  const { _etag, _cachedAt, _fromCache, ...data } = cached;
  return {
    ...data,
    _fromCache: true
  };
}

/**
 * Get cached ETag from localStorage
 * @param {string} siteKey - Site key
 * @returns {string|null} Cached ETag or null
 */
function getETagFromCache(siteKey) {
  try {
    const cacheKey = getCacheKey(siteKey);
    const item = localStorage.getItem(cacheKey);
    if (!item) return null;

    const cacheData = JSON.parse(item);
    return cacheData._etag || null;
  } catch (error) {
    console.warn('EventFoundry: ETag read failed:', error);
    return null;
  }
}

/**
 * Save to localStorage with ETag
 * @param {string} siteKey - Site key
 * @param {Object} data - Event definitions response to cache
 * @param {string|null} etag - ETag header from response
 */
function saveToCache(siteKey, data, etag) {
  try {
    const cacheKey = getCacheKey(siteKey);

    // Normalize ETag by removing W/ prefix if present (Cloudflare adds this)
    const normalizedETag = etag ? etag.replace(/^W\//, '') : null;

    // Combine event data and ETag into single cache object
    const cacheData = {
      ...data,
      _etag: normalizedETag,
      _cachedAt: Date.now()
    };

    // Save combined data
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('EventFoundry: Cache write failed:', error);
  }
}

/**
 * Clear cache for a specific site key
 * @param {string} siteKey - Site key
 */
function clearCache(siteKey) {
  try {
    const cacheKey = getCacheKey(siteKey);
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.warn('EventFoundry: Failed to clear cache:', error);
  }
}

/**
 * Fetch event definitions (exported for testing)
 * @param {string} siteKey - Site key for fetching event definitions
 * @returns {Promise<Object>} Event definitions response object
 */
export { fetchEventDefinitions, initializeDestinations };

/**
 * Reload event definitions (for editor mode)
 * Clears the local cache before fetching so the editor's RELOAD_CONFIG always
 * produces a fresh 200 response — bypassing ETag 304 and version-rollback
 * protection, both of which can fire incorrectly after a DB reset in dev.
 */
export async function reloadEventDefinitions() {
  const config = getScriptConfig();
  if (!config || !config.siteKey) {
    console.error('EventFoundry: No site key available for reload');
    return;
  }

  const { siteKey, devMode } = config;

  console.log('EventFoundry: Reloading definitions');

  // Clear cache before fetching: the editor is authoritative when it sends
  // RELOAD_CONFIG (a write just happened), so we skip ETag and version guards.
  clearCache(siteKey);

  // Fetch fresh definitions
  try {
    const data = await fetchEventDefinitions(siteKey);

    // Re-initialize destinations
    const initResults = await initializeDestinations(data.destinationConfigs);
    const events = data.events;

    // Re-initialize tracking with fresh definitions
    initTracking(events, devMode, initResults);

    if (data._fromSafeEmpty) {
      console.warn('EventFoundry: Definitions reload fell back to safe-empty payload');
    } else if (data._fromCache) {
      console.warn('EventFoundry: Definitions reload used cached payload fallback');
    } else {
      console.log('EventFoundry: Definitions reloaded successfully');
    }
  } catch (error) {
    console.error('EventFoundry: Failed to reload definitions', error);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
